import { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
// Import Services
import { getCustomerJourneyBlockTranslatableErrorText } from 'services/customerJourney/helpers';
import { Helpers } from 'services';
// Import UI Components
import { Button, Icon, Tooltip } from '@geneui/components';
// Import Constants
import {
    l,
    ClientsCountType,
    CustomerJourneyGroupBlockTypes,
    CustomerJourneyStatusLabels,
    CustomerJourneyWorkingState,
} from 'constants/common';
// Import Hooks
import useHasPermission from 'hooks/UseHasPermission';
// Import Version History
import { useVersionHistoryContext } from 'components/VersionHistory';
// Import Enums
import { PermissionEnum } from 'enums';

const { hasLabel } = Helpers;

const BaseBlock = ({ node }) => {
    const { t } = useTranslation();
    const { hasPermission } = useHasPermission();
    const isManagerOrAdmin = hasPermission([PermissionEnum.CRMAdmin, PermissionEnum.CRMManager]);
    const {
        defaultIcon,
        cssClassName,
        actions,
        name: defaultName,
        blockType,
        readOnly,
        customerJourneyStatus,
        workingState,
        blockWorkingState,
        allClientsCounts,
        isFiltered,
        currentClientsCounts,
        isValid,
        errorMessages,
        isEditable,
        isDeletable,
        isCloneable,
        hasInfo,
        totalClientsCount,
        totalClientsRemoveCount,
        clientsCountType,
        removeClientsCounts,
        showRemovedClientsCount,
        targetClientsCount,
        targetClientsRemoveCount,
        metaData,
    } = node.data;

    const blockTitleLabel = l[`CJBlock${blockType}`];

    const [isDisabled, setIsDisabled] = useState(hasLabel(workingState, CustomerJourneyWorkingState.Disabled));
    const [initialIsSkipped] = useState(() => hasLabel(blockWorkingState, CustomerJourneyWorkingState.Skipped));
    const [name, setName] = useState<string>(t(defaultName));
    const { pointInTime } = useVersionHistoryContext();
    const isVersionViewMode = !isNil(pointInTime);
    const isSkippable = metaData?.CustomAttributes?.Skippable;
    const [isSkipped, setIsSkipped] = useState(() => hasLabel(blockWorkingState, CustomerJourneyWorkingState.Skipped));

    const onNameChange = (e) => {
        node.data.name = e.target.value;
        setName(node.data.name);
    };

    const onBlurHandler = () => {
        actions.editNameAction(node);
    };

    const showClientsList = (_e) => {
        actions.showClientsList(node);
    };

    // Keep the disabled flag in sync with `workingState`, which FlowEditor refreshes on the node after mount
    // (the clients counts/working state are now loaded separately from the schema).
    useEffect(() => {
        setIsDisabled(hasLabel(workingState, CustomerJourneyWorkingState.Disabled));
    }, [workingState]);

    const toggleBlockWorkingState = async (_e) => {
        const newState = await actions.toggleBlockWorkingState(node, isDisabled);

        setIsDisabled(newState);
    };

    const toggleBlockSkipState = () => {
        const currentState = node.data.blockWorkingState || 0;
        node.data.blockWorkingState = isSkipped
            ? currentState - CustomerJourneyWorkingState.Skipped
            : currentState + CustomerJourneyWorkingState.Skipped;
        setIsSkipped(!isSkipped);
        actions.refreshElements?.();
    };

    const showBlockInfo = () => {
        actions.showBlockInfo(node);
    };

    useEffect(() => {
        const openedInfoBlockId = actions.getInfoBlockId?.();
        if (openedInfoBlockId && openedInfoBlockId === node.id) {
            showBlockInfo();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const removeElementClickHandler = (_e) => {
        actions.deleteAction([node]);
    };

    const cloneElementClickHandler = (_e) => {
        actions.cloneAction(node);
    };

    const editElementClickHandler = (e) => {
        e.stopPropagation();
        actions.editAction(node);
    };

    const getPercentage = (current, total) => {
        if (total === 0 || isNil(total)) {
            return 0;
        }

        const percent = (current / total) * 100;

        if (percent >= 10) {
            return Math.floor(percent);
        }

        if (percent < 1 && percent > 0) {
            return '<1';
        }

        return +percent.toFixed(1);
    };

    useEffect(() => {
        setName(node.data.name);
    }, [node]);

    const strBlockClientsCount = useMemo(() => {
        const tmpAllClientCounts = showRemovedClientsCount ? allClientsCounts + removeClientsCounts : allClientsCounts;
        return !isFiltered ? tmpAllClientCounts : `${allClientsCounts} / ${currentClientsCounts}`;
    }, [isFiltered, allClientsCounts, currentClientsCounts, showRemovedClientsCount, removeClientsCounts]);

    const percentageCurrentValue = useMemo(() => {
        const tmpAllClientCounts = showRemovedClientsCount
            ? currentClientsCounts + removeClientsCounts
            : currentClientsCounts;

        const totalClients = showRemovedClientsCount ? totalClientsCount + totalClientsRemoveCount : totalClientsCount;
        const targetClients = showRemovedClientsCount
            ? targetClientsCount + targetClientsRemoveCount
            : targetClientsCount;

        const totalPercentage = getPercentage(
            tmpAllClientCounts,
            clientsCountType === ClientsCountType.Total ? totalClients : targetClients,
        );

        return totalPercentage;
    }, [
        clientsCountType,
        currentClientsCounts,
        removeClientsCounts,
        showRemovedClientsCount,
        targetClientsCount,
        targetClientsRemoveCount,
        totalClientsCount,
        totalClientsRemoveCount,
    ]);

    return (
        <Tooltip
            text={getCustomerJourneyBlockTranslatableErrorText(t, errorMessages ?? [])}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            className="crm-flow-editor-block-tooltip"
        >
            <div className={classNames('crm-flow-editor-block-body', cssClassName)}>
                {!isNil(allClientsCounts) && !isVersionViewMode && (
                    <div
                        className={classNames('crm-flow-editor-block-clients-count-container', {
                            'disabled-section': isDisabled,
                        })}
                    >
                        {isDisabled && (
                            <Tooltip text={t(l.Stopped)}>
                                <div className="crm-flow-editor-block-circle-status">
                                    <Icon type="bc-icon-status-stop" />
                                </div>
                            </Tooltip>
                        )}
                        {initialIsSkipped && (
                            <Tooltip text={t(l.Skipped)}>
                                <div className="crm-flow-editor-block-circle-status">
                                    <Icon type="bc-icon-forwards" />
                                </div>
                            </Tooltip>
                        )}
                        {readOnly && node.type !== CustomerJourneyGroupBlockTypes.target && (
                            <div className="crm-flow-editor-block-clients-percentage">{percentageCurrentValue}%</div>
                        )}
                        <div className="crm-flow-editor-block-clients-count">{strBlockClientsCount}</div>
                    </div>
                )}
                <div
                    className={classNames('crm-flow-editor-block-content', {
                        invalid: isValid === false,
                        'disabled-section': readOnly && (isDisabled || isSkipped),
                    })}
                >
                    <div className="crm-flow-editor-block-icon-container">
                        <Icon type={defaultIcon} />
                    </div>
                    <div className="crm-flow-editor-block-title-container">
                        <input
                            type="text"
                            className={classNames('crm-flow-editor-block-title-input', { 'read-only': readOnly })}
                            value={name}
                            title={name}
                            autoFocus={readOnly}
                            disabled={readOnly}
                            onChange={onNameChange}
                            onBlur={onBlurHandler}
                            onPaste={(e) => e.preventDefault()}
                        />
                        <span className="ellipsis-text" title={t(blockTitleLabel)}>
                            {t(blockTitleLabel)}
                        </span>
                    </div>
                </div>
                <div className="crm-flow-editor-block-actions">
                    {readOnly ? (
                        <>
                            {isManagerOrAdmin &&
                                customerJourneyStatus === +CustomerJourneyStatusLabels.InProgress &&
                                !isVersionViewMode && (
                                    <Tooltip text={isDisabled ? t(l.Resume) : t(l.Stop)}>
                                        <button onClick={toggleBlockWorkingState}>
                                            {isDisabled ? (
                                                <Icon type="bc-icon-resume" />
                                            ) : (
                                                <Icon type="bc-icon-status-stop" />
                                            )}
                                        </button>
                                    </Tooltip>
                                )}
                            {!isNil(actions.showClientsList) &&
                                !isNil(allClientsCounts) &&
                                allClientsCounts !== 0 &&
                                !isVersionViewMode && (
                                    <Tooltip text={t(l.ShowClientList)}>
                                        <Button color="default" appearance="minimal" onClick={showClientsList}>
                                            <Icon type="bc-icon-table" />
                                        </Button>
                                    </Tooltip>
                                )}
                            {hasInfo !== false && (
                                <Tooltip text={t(l.Details)}>
                                    <Button color="default" appearance="minimal" onClick={showBlockInfo}>
                                        <Icon type="bc-icon-info-48" />
                                    </Button>
                                </Tooltip>
                            )}
                        </>
                    ) : (
                        <>
                            {isSkippable &&
                                customerJourneyStatus === +CustomerJourneyStatusLabels.InProgress &&
                                !isVersionViewMode && (
                                    <Tooltip text={isSkipped ? t(l.Unskip) : t(l.Skip)}>
                                        <button onClick={toggleBlockSkipState}>
                                            {isSkipped ? (
                                                <Icon type="bc-icon-status-play" />
                                            ) : (
                                                <Icon type="bc-icon-forwards" />
                                            )}
                                        </button>
                                    </Tooltip>
                                )}
                            {isEditable !== false && (
                                <Tooltip text={t(l.Edit)}>
                                    <Button color="default" appearance="minimal" onClick={editElementClickHandler}>
                                        <Icon type="bc-icon-edit-pen" />
                                    </Button>
                                </Tooltip>
                            )}
                            {isCloneable !== false && (
                                <Tooltip text={t(l.Clone)}>
                                    <Button color="default" appearance="minimal" onClick={cloneElementClickHandler}>
                                        <Icon type="bc-icon-clone" />
                                    </Button>
                                </Tooltip>
                            )}
                            {isDeletable !== false && (
                                <Tooltip text={t(l.Delete)}>
                                    <Button
                                        color="default"
                                        appearance="minimal"
                                        className="crm-flow-editor-block-action-delete"
                                        onClick={removeElementClickHandler}
                                    >
                                        <Icon type="bc-icon-delete-2" />
                                    </Button>
                                </Tooltip>
                            )}
                        </>
                    )}
                </div>
            </div>
        </Tooltip>
    );
};

BaseBlock.propTypes = {
    node: PropTypes.object.isRequired, // todo shape validation of
};

export default BaseBlock;
